import type { LocaleCode } from '@/i18n/locales'
import type { AgeBand, Gender } from '@/profile/types'
import type { Extraction } from '@/voice/nlu/extract'
import { extractAgeBand, extractGender } from '@/voice/nlu/extract'
import { normalize, words } from '@/voice/nlu/normalize'

/**
 * Facts nobody was asked for.
 *
 * Age band and gender are not onboarding steps. A general-purpose assistant
 * does not need to know how old somebody is to answer a question about tomato
 * blight, and `docs/ONBOARDING.md` is explicit that both are offered in
 * Settings instead. They are still accepted when volunteered, because
 * somebody who has just said "I am 62 and my knees are bad" should not be
 * asked their age band in Settings a week later.
 *
 * The extractors already exist and are not the difficulty. The difficulty is
 * that running them over open conversation is wrong: `extractGender` matches a
 * bare "he" or "she", so "my brother is a farmer, he is in Kano" records the
 * speaker as a man, and `extractAgeBand` matches any number in range, so
 * "twenty seven bags of rice" records somebody as being in their twenties.
 * Both would be written to the profile at high confidence, never read back,
 * and never noticed.
 *
 * So a fact is only accepted from a first-person frame, and only from the few
 * words that immediately follow it.
 */

export interface VolunteeredFacts {
  ageBand?: Extraction<AgeBand>
  gender?: Extraction<Gender>
}

/**
 * Phrases after which the sentence is about the speaker.
 *
 * Hausa's bare `ni` is included and English's bare `i` is not, and the
 * asymmetry is real rather than an oversight: "Ni mace ce" is the ordinary way
 * to say "I am a woman", whereas English requires the copula, so "i" on its
 * own adds nothing that "i am" does not already cover and matches the filler
 * that recognisers sprinkle through a transcript.
 */
const SELF_FRAMES: Record<LocaleCode, readonly string[]> = {
  en: ['i am', "i'm", 'im', 'my age is', 'i turned', 'i was born in', 'i just turned'],
  ha: ['ina da shekaru', 'shekaruna', 'shekara ta', 'ni'],
}

/**
 * How far past the frame to look, and why the two differ.
 *
 * An age can be spelled out: "sixty two years of age" is five words. A gender
 * is one word and follows the frame immediately in any natural first-person
 * statement, "I am a woman" or "ni mace ce", so it gets a tighter window.
 *
 * Without the difference, "I am talking to my sister, she is a nurse" put the
 * frame at the start of the sentence and "she" five words later, and recorded
 * the speaker as a woman.
 */
const AGE_WINDOW = 5
const GENDER_WINDOW = 3

/**
 * A fact taken this way was inferred from a passing remark, not answered. It
 * is capped below `CONFIRMATION_THRESHOLD` so that anything downstream which
 * reasons about confidence treats it as the guess it is, and so the profile
 * screen shows it as something worth checking rather than something stated.
 */
const VOLUNTEERED_CEILING = 0.6

function afterFrame(transcript: string, locale: LocaleCode, window: number): string | null {
  const text = normalize(transcript)
  if (text.length === 0) return null

  let earliest: { at: number; length: number } | null = null
  for (const frame of SELF_FRAMES[locale]) {
    const needle = normalize(frame)
    // Matched on word boundaries rather than as a substring: "im" inside
    // "important" and "ni" inside "Niamey" are not somebody talking about
    // themselves, and Niamey is a city half this product's users live in.
    const at = indexOfPhrase(text, needle)
    if (at === -1) continue
    if (earliest === null || at < earliest.at) earliest = { at, length: needle.length }
  }
  if (earliest === null) return null

  const tail = text.slice(earliest.at + earliest.length).trim()
  return words(tail).slice(0, window).join(' ')
}

/** Index of a phrase that starts and ends on a word boundary, or -1. */
function indexOfPhrase(text: string, phrase: string): number {
  let from = 0
  for (;;) {
    const at = text.indexOf(phrase, from)
    if (at === -1) return -1
    const before = at === 0 || text[at - 1] === ' '
    const afterAt = at + phrase.length
    const after = afterAt === text.length || text[afterAt] === ' '
    if (before && after) return at
    from = at + 1
  }
}

/**
 * Reads an age band or a gender out of an utterance, if the speaker was
 * plainly talking about themselves. Returns nothing at all otherwise, which is
 * the right answer far more often than it is the cautious one.
 */
export function readVolunteered(transcript: string, locale: LocaleCode): VolunteeredFacts {
  const facts: VolunteeredFacts = {}

  const forAge = afterFrame(transcript, locale, AGE_WINDOW)
  const age = forAge ? extractAgeBand(forAge, locale) : null
  if (age && age.value !== null) {
    facts.ageBand = { ...age, confidence: Math.min(age.confidence, VOLUNTEERED_CEILING) }
  }

  const forGender = afterFrame(transcript, locale, GENDER_WINDOW)
  if (!forGender) return facts
  const gender = extractGender(forGender, locale)
  /**
   * `extractGender` answers "undisclosed" to anything that reads as a refusal,
   * which is right when the question was asked and wrong here. Nobody
   * volunteers a preference not to say about a question that was never put to
   * them, so it is dropped rather than recorded as a decision.
   */
  if (gender.value !== null && gender.value !== 'undisclosed') {
    facts.gender = { ...gender, confidence: Math.min(gender.confidence, VOLUNTEERED_CEILING) }
  }

  return facts
}
